import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Stack,
  Fade,
  Slide,
  Tooltip,
  Divider,
  Badge,
  Tabs,
  Tab,
  Avatar,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Event as EventIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  VerifiedUser as VerifiedIcon,
  ReportProblem as ComplaintIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApplicationTable from '../application/ApplicationTable';
import acProcessService from '../../../services/acProcessService';

const ConvenerDashboard = () => {
  const navigate = useNavigate();
  
  // State for filters
  const [filters, setFilters] = useState({
    acType: '0',
    year: '0',
    month: '1',
    fromDate: '0',
    toDate: '0',
    acType2: '0',
    fromDateCalendar: '',
    toDateCalendar: '',
  });

  const [years, setYears] = useState([]);
  const [acDates, setAcDates] = useState([]);
  const [acDatesTo, setAcDatesTo] = useState([]);
  
  // State for data
  const [faApplications, setFaApplications] = useState([]);
  const [raApplications, setRaApplications] = useState([]);
  const [verificationApplications, setVerificationApplications] = useState([]);
  const [complaints, setComplaints] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState('final-assessment');
  const [searchType, setSearchType] = useState('Date');
  const [appType, setAppType] = useState('Pending'); // 'Pending' or 'Completed'

  // Labels for SAC vs others
  const [labels, setLabels] = useState({
    fa: 'Final Assessment',
    ra: 'Reassessment',
  });

  useEffect(() => {
    // Initialize any default data if needed
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAcTypeChange = async (value) => {
    handleFilterChange('acType', value);
    
    if (value !== '0') {
      try {
        setLoading(true);
        const response = await acProcessService.getACDateYears(value);
        const yearData = Array.isArray(response) ? response : (response.data || response.years || []);
        setYears(yearData);
      } catch (err) {
        setError('Failed to load years');
      } finally {
        setLoading(false);
      }
    }
    
    // Update labels based on AC Type
    if (value === 'SAC') {
      setLabels({
        fa: 'Onsite Surveillance',
        ra: 'Desktop Surveillance',
      });
    } else {
      setLabels({
        fa: 'Final Assessment',
        ra: 'Reassessment/Surveillance',
      });
    }
  };

  const handleMonthChange = async (value) => {
    handleFilterChange('month', value);
    
    if (filters.acType !== '0' && filters.year !== '0') {
      try {
        setLoading(true);
        const response = await acProcessService.getACDatesByYearMonth(
          filters.acType,
          filters.year,
          value
        );
        // Response structure: {status: "success", data: [...], message: "..."}
        const dateData = Array.isArray(response) ? response : (response.data || []);
        console.log('AC Dates loaded:', dateData);
        setAcDates(dateData);
      } catch (err) {
        console.error('Error loading dates:', err);
        setError('Failed to load dates');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFromDateChange = async (value) => {
    handleFilterChange('fromDate', value);
    
    if (value !== '0') {
      try {
        setLoading(true);
        setAcDatesTo([]); // Clear previous data
        
        // Convert to number for comparison since SQL returns numeric IDs
        const numericValue = typeof value === 'string' ? parseInt(value) : value;
        const selectedDate = acDates.find(d => {
          const dateId = d.Id || d.id;
          return dateId === numericValue || dateId === value;
        });
        
        if (!selectedDate) {
          console.error('Selected date not found in acDates array');
          setError('Selected date not found');
          return;
        }
        
        const dateValue = selectedDate.Date || selectedDate.date;
        
        console.log('Selected Date Object:', selectedDate);
        console.log('Passing to API - AC Type:', filters.acType, 'Date:', dateValue);
        
        const response = await acProcessService.getACDateToByDate(
          filters.acType,
          dateValue
        );
        
        console.log('Full API Response:', response);
        console.log('Response status:', response.status);
        console.log('Response data field:', response.data);
        
        // Handle response structure: {status: "success", data: [...], message: "..."}
        let dateToData = [];
        
        if (response && response.data) {
          dateToData = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          dateToData = response;
        }
        
        console.log('Date To Data Array:', dateToData);
        console.log('Number of records:', dateToData.length);
        
        if (dateToData.length === 0) {
          setError('No end dates found for the selected date');
        }
        
        setAcDatesTo(dateToData);
      } catch (err) {
        console.error('Error loading end dates:', err);
        console.error('Error details:', err.response?.data || err.message);
        setError('Failed to load end dates: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    } else {
      setAcDatesTo([]);
    }
  };

  const handleSearch = async () => {
    if (filters.acType === '0' || filters.year === '0' || filters.month === '0' ||
        filters.fromDate === '0' || filters.toDate === '0') {
      setError('Please select all required fields');
      return;
    }

    setSearchType('Date');
    setError('');
    
    // Update labels based on AC Type
    if (filters.acType === 'SAC') {
      setLabels({
        fa: 'Onsite Surveillance',
        ra: 'Desktop Surveillance',
      });
    } else {
      setLabels({
        fa: 'Final Assessment',
        ra: 'Reassessment/Surveillance',
      });
    }
    
    await fetchApplications('Date');
  };

  const handleSearch2 = async () => {
    if (filters.acType2 === '0' || !filters.fromDateCalendar || !filters.toDateCalendar) {
      setError('Please select all required fields');
      return;
    }

    setSearchType('Calendar');
    setError('');
    
    // Update labels based on AC Type
    if (filters.acType2 === 'SAC') {
      setLabels({
        fa: 'Onsite Surveillance',
        ra: 'Desktop Surveillance',
      });
    } else {
      setLabels({
        fa: 'Final Assessment',
        ra: 'Reassessment/Surveillance',
      });
    }
    
    await fetchApplications('Calendar');
  };

  const fetchApplications = async (type) => {
    try {
      setLoading(true);
      setShowResults(true);
      
      let acType, acDate, acDateTo;
      
      if (type === 'Date') {
        acType = filters.acType;
        const selectedFromDate = acDates.find(d => d.Id === filters.fromDate);
        const selectedToDate = acDatesTo.find(d => (d.Id || d.id) === filters.toDate);
        acDate = selectedFromDate?.Date || filters.fromDate;
        acDateTo = selectedToDate?.DateTo || filters.toDate;
      } else {
        acType = filters.acType2;
        acDate = filters.fromDateCalendar;
        acDateTo = filters.toDateCalendar;
      }

      const isSAC = acType === 'SAC';
      
      let faCondition, raCondition, verCondition, complaintCondition;
      
      if (appType === 'Pending') {
        console.log('pending conditions for AC Type:', acType);
        // Final Assessment - Pending
        if (isSAC) {
          faCondition = " AND ac.Is_UnderProcess=0 AND isnull(ac.Is_Active,0)=0 AND la.Stage IN('6d','6e') AND ac.appid NOT IN (SELECT appid FROM AdverseAction WHERE Appid=ac.appid AND IsActive=0) AND la.AssessmentTypeId IN (1,4)";
        } else {
          faCondition = " AND ac.Is_UnderProcess=0 AND isnull(ac.Is_Active,0)=0 AND la.Stage IN('6d','6e') AND ac.appid NOT IN (SELECT appid FROM AdverseAction WHERE Appid=ac.appid AND IsActive=0) AND la.AssessmentTypeId=1";
        }
        
        // Reassessment - Pending
        raCondition = " AND ac.Is_UnderProcess=0 AND ac.appid NOT IN (SELECT appid FROM AdverseAction WHERE Appid=ac.appid AND IsActive=0) AND isnull(ac.Is_Active,0)=0 AND la.Stage IN('6d','6e') AND la.AssessmentTypeId IN (2,3,4)";
        if (isSAC) {
          raCondition += " AND la.AssessmentTypeId!=4"; // Exclude type 4 for SAC
        }
        
        // Verification - Pending (different logic: Is_UnderProcess=1 OR types 5,7,6 OR AdverseAction)
        verCondition = " AND la.Stage IN('6d','6e') AND (ac.Is_UnderProcess=1 OR la.AssessmentTypeId IN (5,7,6) OR ac.appid IN (SELECT appid FROM AdverseAction WHERE Appid=ac.appid AND IsActive=0)) AND isnull(ac.Is_Active,0)=0 AND la.AssessmentTypeId IN(1,2,3,4,5,7,6)";
        
        // Complaint - Pending
        complaintCondition = " AND ac.momfile IS NULL AND la.AssessmentTypeId=6";
        
      } else {
        console.log('completed conditions for AC Type:', acType);
        // Final Assessment - Completed
        if (isSAC) {
          faCondition = " AND ac.Is_UnderProcess=0 AND isnull(ac.Is_Active,0)=0 AND la.Stage IN('7','7a','7b','7c','7d','7e') AND ac.appid NOT IN (SELECT appid FROM AdverseAction WHERE Appid=ac.appid AND IsActive=0) AND la.AssessmentTypeId IN (1,4)";
        } else {
          faCondition = " AND ac.Is_UnderProcess=0 AND isnull(ac.Is_Active,0)=0 AND la.Stage IN('7','7a','7b','7c','7d','7e') AND ac.appid NOT IN (SELECT appid FROM AdverseAction WHERE Appid=ac.appid AND IsActive=0) AND la.AssessmentTypeId=1";
        }
        
        // Reassessment - Completed
        raCondition = " AND ac.Is_UnderProcess=0 AND ac.appid NOT IN (SELECT appid FROM AdverseAction WHERE Appid=ac.appid AND IsActive=0) AND isnull(ac.Is_Active,0)=0 AND la.Stage IN('7','7a','7b','7c','7d','7e') AND la.AssessmentTypeId IN (2,3,4)";
        if (isSAC) {
          raCondition += " AND la.AssessmentTypeId!=4"; // Exclude type 4 for SAC
        }
        
        // Verification - Completed
        verCondition = " AND la.Stage IN('7') AND (ac.Is_UnderProcess=1 OR la.AssessmentTypeId IN (5,7) OR ac.appid IN (SELECT appid FROM AdverseAction WHERE Appid=ac.appid AND IsActive=0)) AND isnull(ac.Is_Active,0)=0 AND la.AssessmentTypeId IN(1,2,3,4,5,7)";
        
        // Complaint - Completed
        complaintCondition = " AND ac.momfile IS NOT NULL AND la.AssessmentTypeId=6";
      }

      const conditions = {
        finalAssessment: faCondition,
        reassessment: raCondition,
        verification: verCondition,
        complaint: complaintCondition
      };

      const [faResponse, raResponse, verResponse, complaintResponse] = await Promise.all([
        acProcessService.getApplicationsWithCondition(acType, acDate, acDateTo, conditions.finalAssessment),
        acProcessService.getApplicationsWithCondition(acType, acDate, acDateTo, conditions.reassessment),
        acProcessService.getApplicationsWithCondition(acType, acDate, acDateTo, conditions.verification),
        !isSAC 
          ? acProcessService.getApplicationsWithCondition(acType, acDate, acDateTo, conditions.complaint)
          : Promise.resolve([]),
      ]);

      const faData = Array.isArray(faResponse) ? faResponse : (faResponse.data || []);
      const raData = Array.isArray(raResponse) ? raResponse : (raResponse.data || []);
      const verData = Array.isArray(verResponse) ? verResponse : (verResponse.data || []);
      const complaintData = Array.isArray(complaintResponse) ? complaintResponse : (complaintResponse.data || []);

      setFaApplications(faData);
      setRaApplications(raData);
      setVerificationApplications(verData);
      setComplaints(complaintData);
    } catch (err) {
      setError('Failed to fetch applications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleDownload = () => {
    const params = searchType === 'Calendar'
      ? `?t=calendar&acType=${filters.acType2}&fromDate=${filters.fromDateCalendar}&toDate=${filters.toDateCalendar}`
      : `?t=Date&acDate=${filters.fromDate}&acType=${filters.acType}`;
    
    navigate(`/convener/report${params}`);
  };

  const handleUnderProcess = async () => {
    setAppType('Pending');
    if (showResults) {
      // Update labels based on current AC Type
      const currentAcType = searchType === 'Date' ? filters.acType : filters.acType2;
      if (currentAcType === 'SAC') {
        setLabels({
          fa: 'Onsite Surveillance',
          ra: 'Desktop Surveillance',
        });
      } else {
        setLabels({
          fa: 'Final Assessment',
          ra: 'Reassessment/Surveillance',
        });
      }
      await fetchApplications(searchType);
    }
  };

  const handleCompleted = async () => {
    setAppType('Completed');
    if (showResults) {
      // Update labels based on current AC Type
      const currentAcType = searchType === 'Date' ? filters.acType : filters.acType2;
      if (currentAcType === 'SAC') {
        setLabels({
          fa: 'Onsite Surveillance',
          ra: 'Desktop Surveillance',
        });
      } else {
        setLabels({
          fa: 'Final Assessment',
          ra: 'Reassessment/Surveillance',
        });
      }
      await fetchApplications(searchType);
    }
  };

  const acTypeOptions = [
    { value: '0', label: '---Select AC Type---' },
    { value: 'SEC', label: 'SEC' },
    { value: 'CAT', label: 'CAT' },
    { value: 'CAM', label: 'CAM' },
    { value: 'CAC', label: 'CAC' },
    { value: 'SAC', label: 'SAC' },
    { value: 'CAT (Integrated)', label: 'CAT (Integrated)' },
    { value: 'PTP', label: 'PTP' },
    { value: 'RMP', label: 'RMP' },
    { value: 'Review Committee', label: 'Review Committee' },
  ];

  const monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  return (
    <Box sx={{ 
      p: 2, 
      background: 'linear-gradient(to bottom, #f0f4f8 0%, #e8eef5 100%)',
      minHeight: '100vh' 
    }}>
      {/* Modern Header with Gradient Background */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)',
          color: 'white',
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          {/* Header Content */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  mr: 2,
                  width: 56,
                  height: 56,
                }}
              >
                <DashboardIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Convener Dashboard
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Manage Assessment Committee Applications and Processes
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Fade in={true} timeout={1000}>
        <Card 
          elevation={6} 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            background: 'linear-gradient(to right, #ffffff 0%, #f8f9fa 100%)',
            border: '1px solid rgba(0,0,0,0.05)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
            },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 2 }}>
              <Tooltip title="Upload minutes of meeting" arrow>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/main/convener/upload-mom')}
                  size="large"
                  startIcon={<UploadIcon />}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  Upload MOM
                </Button>
              </Tooltip>
              <Tooltip title="Manage agenda dates" arrow>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/main/convener/agenda-date')}
                  size="large"
                  startIcon={<EditIcon />}
                  color="secondary"
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  Change Agenda
                </Button>
              </Tooltip>
              <Tooltip title="View AC dates" arrow>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/main/convener/ac-dates')}
                  size="large"
                  startIcon={<EventIcon />}
                  color="info"
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  AC Dates
                </Button>
              </Tooltip>
          </Stack>
        </CardContent>
      </Card>
      </Fade>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search Form - Method 1 */}
      <Fade in={true} timeout={1200}>
        <Card 
          elevation={6} 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            background: 'linear-gradient(to bottom right, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(102, 126, 234, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 12px 28px rgba(102, 126, 234, 0.15)',
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 4,
                pb: 2,
                borderBottom: '2px solid',
                borderImage: 'linear-gradient(to right, #667eea, #764ba2) 1',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                }}
              >
                <SearchIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                  Search by AC Date Selection
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f8c8d', mt: 0.5 }}>
                  Select AC type, year, month and date range
                </Typography>
              </Box>
            </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>AC Type</InputLabel>
                <Select
                  value={filters.acType}
                  label="AC Type"
                  onChange={(e) => handleAcTypeChange(e.target.value)}
                >
                  {acTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={filters.year}
                  label="Year"
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                  disabled={filters.acType === '0'}
                >
                  <MenuItem value="0">---Select Year---</MenuItem>
                  {years.map(yearObj => {
                    const yearValue = yearObj.year || yearObj;
                    return (
                      <MenuItem key={yearValue} value={yearValue}>
                        {yearValue}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select
                  value={filters.month}
                  label="Month"
                  onChange={(e) => handleMonthChange(e.target.value)}
                  disabled={filters.year === '0'}
                >
                  {monthOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>From Date</InputLabel>
                <Select
                  value={filters.fromDate}
                  label="From Date"
                  onChange={(e) => handleFromDateChange(e.target.value)}
                  disabled={filters.month === '0'}
                >
                  <MenuItem value="0">-- Select Date --</MenuItem>
                  {acDates.map((date, idx) => {
                    // Handle both uppercase and lowercase field names from SQL
                    const dateId = date.Id || date.id || date.ID || idx;
                    const dateDisplay = date.Date || date.date || date.DATE || 'N/A';
                    return (
                      <MenuItem key={dateId} value={dateId}>
                        {dateDisplay}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>To Date</InputLabel>
                <Select
                  value={filters.toDate}
                  label="To Date"
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  disabled={filters.fromDate === '0'}
                >
                  <MenuItem value="0">-- Select Date --</MenuItem>
                  {acDatesTo.map((date, idx) => {
                    // Handle different response formats: Id/id/ID, DateTo/dateTo/DATE_TO
                    const dateId = date.Id || date.id || date.ID || idx;
                    const dateTo = date.DateTo || date.dateTo || date.DATE_TO || date.Date || date.date || 'N/A';
                    return (
                      <MenuItem key={dateId} value={dateId}>
                        {dateTo}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                size="large"
                sx={{
                  py: 1.8,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      </Fade>

      {/* OR Divider */}
      <Box sx={{ textAlign: 'center', my: 4, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(to right, transparent, #e0e0e0 20%, #e0e0e0 80%, transparent)',
            transform: 'translateY(-50%)',
          }}
        />
        <Chip 
          label="OR" 
          sx={{ 
            fontSize: 18, 
            fontWeight: 700, 
            px: 4, 
            py: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
            border: '3px solid white',
            position: 'relative',
            zIndex: 1,
          }} 
        />
      </Box>

      {/* Search Form - Method 2 */}
      <Fade in={true} timeout={1400}>
        <Card 
          elevation={6} 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            background: 'linear-gradient(to bottom right, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(102, 126, 234, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 12px 28px rgba(102, 126, 234, 0.15)',
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 4,
                pb: 2,
                borderBottom: '2px solid',
                borderImage: 'linear-gradient(to right, #667eea, #764ba2) 1',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #FF6B6B 0%, #EE5A24 100%)',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(238, 90, 36, 0.3)',
                }}
              >
                <CalendarIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                  Search by Calendar Date Range
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f8c8d', mt: 0.5 }}>
                  Select AC type and custom date range
                </Typography>
              </Box>
            </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>AC Type</InputLabel>
                <Select
                  value={filters.acType2}
                  label="AC Type"
                  onChange={(e) => handleFilterChange('acType2', e.target.value)}
                >
                  {acTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                value={filters.fromDateCalendar}
                onChange={(e) => handleFilterChange('fromDateCalendar', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                value={filters.toDateCalendar}
                onChange={(e) => handleFilterChange('toDateCalendar', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch2}
                disabled={loading}
                size="large"
                sx={{
                  py: 1.8,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #FF6B6B 0%, #EE5A24 100%)',
                  boxShadow: '0 4px 15px rgba(238, 90, 36, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #EE5A24 0%, #FF6B6B 100%)',
                    boxShadow: '0 6px 20px rgba(238, 90, 36, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      </Fade>

      {/* Filter Tabs */}
      {showResults && (
        <Fade in={showResults} timeout={800}>
          <Card 
            elevation={6} 
            sx={{ 
              mb: 4, 
              borderRadius: 3,
              background: 'white',
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#2c3e50' }}>
                Filter by Category
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Tabs
                    value={appType}
                    onChange={(e, newValue) => {
                      if (newValue === 'Pending') {
                        handleUnderProcess();
                      } else {
                        handleCompleted();
                      }
                    }}
                    sx={{
                      minHeight: 48,
                      '& .MuiTab-root': {
                        minHeight: 48,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        transition: 'all 0.3s ease',
                      },
                      '& .Mui-selected': {
                        color: '#fff !important',
                      },
                      '& .MuiTabs-indicator': {
                        height: '100%',
                        borderRadius: 0,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        zIndex: 0,
                      },
                    }}
                  >
                    <Tab 
                      label="Under Process" 
                      value="Pending"
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                      }}
                    />
                    <Tab 
                      label="Completed" 
                      value="Completed"
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                      }}
                    />
                  </Tabs>
                </Paper>
                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 2 ,mt: 1}}>
              <Badge 
                badgeContent={faApplications.length} 
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    px: 1,
                  },
                }}
              >
                <Button
                  variant={activeTab === 'final-assessment' ? 'contained' : 'outlined'}
                  onClick={() => handleTabChange('final-assessment')}
                  size="large"
                  startIcon={<AssignmentIcon />}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    transition: 'all 0.3s ease',
                    ...(activeTab === 'final-assessment' ? {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    } : {}),
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {labels.fa}
                </Button>
              </Badge>
              <Badge 
                badgeContent={raApplications.length} 
                color="secondary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    px: 1,
                  },
                }}
              >
                <Button
                  variant={activeTab === 'reassessment' ? 'contained' : 'outlined'}
                  onClick={() => handleTabChange('reassessment')}
                  size="large"
                  color="secondary"
                  startIcon={<AssignmentIcon />}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    transition: 'all 0.3s ease',
                    ...(activeTab === 'reassessment' ? {
                      boxShadow: '0 4px 15px rgba(156, 39, 176, 0.4)',
                    } : {}),
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {labels.ra}
                </Button>
              </Badge>
              <Badge 
                badgeContent={verificationApplications.length} 
                color="success"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    px: 1,
                  },
                }}
              >
                <Button
                  variant={activeTab === 'verification' ? 'contained' : 'outlined'}
                  onClick={() => handleTabChange('verification')}
                  size="large"
                  color="success"
                  startIcon={<VerifiedIcon />}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    transition: 'all 0.3s ease',
                    ...(activeTab === 'verification' ? {
                      boxShadow: '0 4px 15px rgba(46, 125, 50, 0.4)',
                    } : {}),
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Verification or other
                </Button>
              </Badge>
              {filters.acType !== 'SAC' && filters.acType2 !== 'SAC' && (
                <Badge 
                  badgeContent={complaints.length} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      px: 1,
                    },
                  }}
                >
                  <Button
                    variant={activeTab === 'complaint' ? 'contained' : 'outlined'}
                    onClick={() => handleTabChange('complaint')}
                    size="large"
                    color="error"
                    startIcon={<ComplaintIcon />}
                    sx={{
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderWidth: 2,
                      transition: 'all 0.3s ease',
                      ...(activeTab === 'complaint' ? {
                        boxShadow: '0 4px 15px rgba(211, 47, 47, 0.4)',
                      } : {}),
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Complaint
                  </Button>
                </Badge>
              )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Results Section */}
      {showResults && (
        <Box>
          {/* Final Assessment Section */}
          {activeTab === 'final-assessment' && (
            <Fade in={activeTab === 'final-assessment'} timeout={600}>
              <Card 
                elevation={6} 
                sx={{ 
                  mb: 3, 
                  borderRadius: 3,
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          background: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <AssignmentIcon sx={{ fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {labels.fa}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total applications in this category
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={`${faApplications.length} Records`} 
                      sx={{ 
                        background: 'rgba(255,255,255,0.9)',
                        color: '#667eea',
                        fontWeight: 700,
                        fontSize: '1rem',
                        px: 2,
                        py: 2.5,
                      }} 
                    />
                  </Box>
                </Box>
                <CardContent sx={{ p: 3 }}>
                <ApplicationTable
                  data={faApplications}
                  loading={loading}
                  type="final-assessment"
                />
              </CardContent>
            </Card>
          </Fade>
          )}

          {/* Reassessment Section */}
          {activeTab === 'reassessment' && (
            <Fade in={activeTab === 'reassessment'} timeout={600}>
              <Card 
                elevation={6} 
                sx={{ 
                  mb: 3, 
                  borderRadius: 3,
                  border: '1px solid rgba(156, 39, 176, 0.2)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          background: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <AssignmentIcon sx={{ fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {labels.ra}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total applications in this category
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={`${raApplications.length} Records`} 
                      sx={{ 
                        background: 'rgba(255,255,255,0.9)',
                        color: '#9c27b0',
                        fontWeight: 700,
                        fontSize: '1rem',
                        px: 2,
                        py: 2.5,
                      }} 
                    />
                  </Box>
                </Box>
                <CardContent sx={{ p: 3 }}>
                  <ApplicationTable
                    data={raApplications}
                    loading={loading}
                    type="reassessment"
                  />
                </CardContent>
              </Card>
            </Fade>
          )}

          {/* Verification Section */}
          {activeTab === 'verification' && (
            <Fade in={activeTab === 'verification'} timeout={600}>
              <Card 
                elevation={6} 
                sx={{ 
                  mb: 3, 
                  borderRadius: 3,
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          background: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <VerifiedIcon sx={{ fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Verification or any other
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total applications in this category
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={`${verificationApplications.length} Records`} 
                      sx={{ 
                        background: 'rgba(255,255,255,0.9)',
                        color: '#4caf50',
                        fontWeight: 700,
                        fontSize: '1rem',
                        px: 2,
                        py: 2.5,
                      }} 
                    />
                  </Box>
                </Box>
                <CardContent sx={{ p: 3 }}>
                  <ApplicationTable
                    data={verificationApplications}
                    loading={loading}
                    type="verification"
                  />
                </CardContent>
              </Card>
            </Fade>
          )}

          {/* Complaints Section */}
          {activeTab === 'complaint' && filters.acType !== 'SAC' && filters.acType2 !== 'SAC' && (
            <Fade in={activeTab === 'complaint'} timeout={600}>
              <Card 
                elevation={6} 
                sx={{ 
                  mb: 3, 
                  borderRadius: 3,
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, #f44336 0%, #e57373 100%)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          background: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <ComplaintIcon sx={{ fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Complaint
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total applications in this category
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={`${complaints.length} Records`} 
                      sx={{ 
                        background: 'rgba(255,255,255,0.9)',
                        color: '#f44336',
                        fontWeight: 700,
                        fontSize: '1rem',
                        px: 2,
                        py: 2.5,
                      }} 
                    />
                  </Box>
                </Box>
                <CardContent sx={{ p: 3 }}>
                  <ApplicationTable
                    data={complaints}
                    loading={loading}
                    type="complaint"
                  />
                </CardContent>
              </Card>
            </Fade>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ConvenerDashboard;
